/**
 * AgentChatHistory
 *
 * "Recent activity with {agent}" — the list DwAgentChatBar shows in its empty
 * state when it's opened full-size as the New Chat page, in three tabs:
 * chats, Command Center commands and routine runs. Clicking a row opens it
 * the same way the Digital Workforce activity lists do (chat → full chat
 * page, command → Command Center, run → its routine).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiMessageSquare, FiChevronRight, FiClipboard, FiRepeat } from 'react-icons/fi';
import {
  getAgentChatsPage, getAgentKanbanTasks, getAgentRoutineRuns,
  type AgentChatRecord, type AgentKanbanTaskRecord, type AgentRoutineRunRecord,
} from '../../features/claudeagents/claudeAgentApi';
import { resolveTaskTransactionId } from '../../services/kanbanAIService';
import { parseApiDate, parseKanbanDate } from '../../utils/dateTime';
import { stripTemplateMarkers } from '../../features/chat/entityLinks';
import { quickPromptMarkersToText } from '../../utils/proceedPrompt';
import { ROUTES } from '../../app/routes';
import { CHAT_ARCHIVED_EVENT } from '../../services/historyService';

interface AgentChatHistoryProps {
  agentId: string;
  agentName: string;
}

// "Today, 2:14 PM" / "Yesterday" / "Monday" / "Sep 22" — chats are scanned
// by recency, so the nearer the date, the more precise the label.
export function formatChatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(today) - startOfDay(d)) / 86_400_000);
  if (days === 0) return `Today, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], {
    month: 'short', day: 'numeric',
    ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
}

const PAGE_SIZE = 20;

type HistoryTab = 'chats' | 'commands' | 'runs';
const TABS: { key: HistoryTab; label: string }[] = [
  { key: 'chats', label: 'Chats' },
  { key: 'commands', label: 'Commands' },
  { key: 'runs', label: 'Routine runs' },
];

// Command status comes back as the Kanban board's status GUID.
const TASK_STATUS: Record<string, { label: string; color: string }> = {
  '11111111-1111-1111-1111-111111111111': { label: 'To Do', color: '#9ca3af' },
  '22222222-2222-2222-2222-222222222222': { label: 'In Progress', color: '#f59e0b' },
  '33333333-3333-3333-3333-333333333333': { label: 'In Review', color: '#00b0d3' },
  '44444444-4444-4444-4444-444444444444': { label: 'Done', color: '#22c55e' },
};
const RUN_STATUS_COLOR: Record<string, string> = {
  completed: '#22c55e', success: '#22c55e', succeeded: '#22c55e', done: '#22c55e',
  running: '#f59e0b', in_progress: '#f59e0b', queued: '#9ca3af', pending: '#9ca3af',
  failed: '#ef4444', error: '#ef4444', cancelled: '#9ca3af', canceled: '#9ca3af',
};
const runStatus = (status?: string) => {
  const key = (status ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return null;
  const label = key.replace(/_/g, ' ');
  return { label: label.charAt(0).toUpperCase() + label.slice(1), color: RUN_STATUS_COLOR[key] ?? '#9ca3af' };
};
// Titles and descriptions come back as agent markdown (headings, bold,
// tables, entity markers) — rows show them as one line of plain text.
const toPlainLines = (text?: string): string[] =>
  stripTemplateMarkers(quickPromptMarkersToText(
    (text ?? '').replace(/\$\$[IP]DF_([^#$]+)##[^$]*\$\$/g, (_, name: string) => name.trim()),
  ))
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    // Streamed run notes glue a heading onto the previous sentence
    // ("Email sent.## Summary") — start it on its own line.
    .replace(/([^\n])[ \t]*(#{2,6}[ \t])/g, '$1\n$2')
    .replace(/([.!?:])[ \t]*(#[ \t])/g, '$1\n$2')
    // …and sentences with no space between them ("list.Now sending").
    .replace(/([a-z0-9)][.!?])([A-Z])/g, '$1 $2')
    .split('\n')
    .map((line) => line
      .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+)/, '')
      .replace(/^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/, '')
      .replace(/^\s*(?:[-*_]\s*){3,}$/, '')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/(\*\*|__)(.+?)\1/g, '$2')
      .replace(/(^|[\s(])[*_]([^*_\s][^*_]*?)[*_](?=[\s).,:;!?]|$)/g, '$1$2')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/\s*\|\s*/g, ' · ')
      .replace(/^[\s·]+|[\s·]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim())
    .filter(Boolean);
const plainText = (text?: string) => toPlainLines(text).join(' ');
// First line of the run notes, as the Digital Workforce run list shows it.
const runNotesSummary = (notes?: string) => {
  const first = toPlainLines(notes)[0] ?? '';
  if (!first) return 'Routine run';
  return first.length > 100 ? `${first.slice(0, 100).trim()}…` : first;
};

const StatusTag: React.FC<{ status: { label: string; color: string } | null }> = ({ status }) =>
  status ? (
    <span className="rql-pcb__history-status">
      <span className="rql-pcb__history-status-dot" style={{ background: status.color }} aria-hidden="true" />
      {status.label}
    </span>
  ) : null;

const HistorySkeleton: React.FC = () => (
  <ul className="rql-pcb__history-list" aria-busy="true">
    {Array.from({ length: 3 }).map((_, i) => (
      <li key={i} className="rql-pcb__history-skel">
        <div className="chat-skel-bone" style={{ width: '45%', height: 12 }} />
        <div className="chat-skel-bone" style={{ width: '18%', height: 10 }} />
      </li>
    ))}
  </ul>
);

const AgentChatHistory: React.FC<AgentChatHistoryProps> = ({ agentId, agentName }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<HistoryTab>('chats');
  const [chats, setChats] = useState<AgentChatRecord[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const loadingRef = useRef(false);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  // Commands and runs load the first time their tab opens (last 7 days —
  // the endpoints' default range, same as the Digital Workforce lists).
  const [tasks, setTasks] = useState<AgentKanbanTaskRecord[] | null>(null);
  const [tasksFailed, setTasksFailed] = useState(false);
  const [runs, setRuns] = useState<AgentRoutineRunRecord[] | null>(null);
  const [runsFailed, setRunsFailed] = useState(false);
  const [openingTaskId, setOpeningTaskId] = useState<string | null>(null);

  // First page. The endpoint returns sessions newest-activity first, PAGE_SIZE
  // at a time; a full page means there may be more.
  useEffect(() => {
    let cancelled = false;
    setChats(null);
    setLoadFailed(false);
    setHasMore(false);
    setMoreFailed(false);
    setTasks(null);
    setTasksFailed(false);
    setRuns(null);
    setRunsFailed(false);
    getAgentChatsPage(agentId, PAGE_SIZE)
      .then((page) => {
        if (cancelled) return;
        setChats(page.items);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        if (cancelled) return;
        setChats([]);
        setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [agentId]);

  // An archived chat (sidebar, Chat History) leaves this list right away.
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (id) setChats((prev) => prev && prev.filter((c) => c.id !== id));
    };
    window.addEventListener(CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(CHAT_ARCHIVED_EVENT, onArchived);
  }, []);

  useEffect(() => {
    if (tab !== 'commands' || tasks !== null) return;
    let cancelled = false;
    getAgentKanbanTasks(agentId, agentName, PAGE_SIZE)
      .then((rows) => {
        if (cancelled) return;
        const at = (t: AgentKanbanTaskRecord) => parseKanbanDate(t.created_at)?.getTime() ?? 0;
        setTasks([...rows].sort((a, b) => at(b) - at(a)));
      })
      .catch(() => {
        if (cancelled) return;
        setTasks([]);
        setTasksFailed(true);
      });
    return () => { cancelled = true; };
  }, [tab, tasks, agentId, agentName]);

  useEffect(() => {
    if (tab !== 'runs' || runs !== null) return;
    let cancelled = false;
    getAgentRoutineRuns(agentId, agentName, PAGE_SIZE)
      .then((rows) => {
        if (cancelled) return;
        const at = (r: AgentRoutineRunRecord) => parseApiDate(r.started_at || r.finished_at)?.getTime() ?? 0;
        setRuns([...rows].sort((a, b) => at(b) - at(a)));
      })
      .catch(() => {
        if (cancelled) return;
        setRuns([]);
        setRunsFailed(true);
      });
    return () => { cancelled = true; };
  }, [tab, runs, agentId, agentName]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore || !chats) return;
    loadingRef.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const requestedFor = agentId;
    getAgentChatsPage(agentId, PAGE_SIZE, chats.length)
      .then((page) => {
        if (requestedFor !== agentId) return;
        setChats((prev) => {
          const seen = new Set((prev ?? []).map((c) => c.id));
          return [...(prev ?? []), ...page.items.filter((r) => !seen.has(r.id))];
        });
        setHasMore(page.hasMore);
      })
      .catch(() => setMoreFailed(true))
      .finally(() => {
        loadingRef.current = false;
        setLoadingMore(false);
      });
  }, [agentId, chats, hasMore]);

  // Load the next page when the end of the (scrollable) list comes into view.
  useEffect(() => {
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore || moreFailed) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root, rootMargin: '0px 0px 120px 0px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, moreFailed, loadMore, chats, tab]);

  const openChat = (chat: AgentChatRecord) => {
    navigate(`${ROUTES.CHAT}/${chat.id}`, {
      state: { dwAgentId: agentId, dwAgentName: agentName, dwSessionId: chat.id },
    });
  };

  // Same hand-off as the Digital Workforce task list: open the command by its
  // transaction id when it resolves, otherwise by task id.
  const openTask = async (task: AgentKanbanTaskRecord) => {
    if (openingTaskId) return;
    setOpeningTaskId(String(task.id));
    const txId = await resolveTaskTransactionId(String(task.id));
    setOpeningTaskId(null);
    navigate(txId
      ? `${ROUTES.MOMENTUM_COMMAND}/${encodeURIComponent(txId)}`
      : `${ROUTES.MOMENTUM_COMMAND}?taskId=${task.id}`);
  };

  const openRun = (run: AgentRoutineRunRecord) => {
    if (run.routine_id) navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(run.routine_id)}`);
  };

  // Full run notes in a hover card (the row only fits the first line). It
  // renders in a portal so the scrolling list doesn't clip it, and stays open
  // while the pointer moves from the row into the card so long notes scroll.
  const [notesCard, setNotesCard] = useState<{
    runId: string; title: string; lines: string[]; top: number; left: number; width: number; above: boolean;
  } | null>(null);
  const notesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notesCardRef = useRef<HTMLDivElement>(null);
  const keepNotesCard = () => {
    if (notesCloseTimer.current) clearTimeout(notesCloseTimer.current);
    notesCloseTimer.current = null;
  };
  const hideNotesCard = () => {
    keepNotesCard();
    notesCloseTimer.current = setTimeout(() => setNotesCard(null), 120);
  };
  const showNotesCard = (el: HTMLElement, run: AgentRoutineRunRecord) => {
    keepNotesCard();
    const lines = toPlainLines(run.notes);
    if (lines.length === 0) { setNotesCard(null); return; }
    const rect = el.getBoundingClientRect();
    const width = Math.min(rect.width, 560);
    const above = window.innerHeight - rect.bottom < 240 && rect.top > window.innerHeight - rect.bottom;
    setNotesCard({
      runId: String(run.id),
      title: plainText(run.name) || 'Routine run notes',
      lines,
      top: above ? rect.top - 6 : rect.bottom + 6,
      left: Math.max(8, rect.right - width),
      width,
      above,
    });
  };
  useEffect(() => {
    if (!notesCard) return;
    const close = () => setNotesCard(null);
    // Scrolling the page or list moves the row away; scrolling the card doesn't.
    const onScroll = (e: Event) => {
      if (!notesCardRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [notesCard]);
  useEffect(() => () => keepNotesCard(), []);
  useEffect(() => { setNotesCard(null); }, [tab]);

  const tabIcon = tab === 'commands'
    ? <FiClipboard size={12} />
    : tab === 'runs' ? <FiRepeat size={12} /> : <FiMessageSquare size={12} />;

  return (
    <section className="rql-pcb__history" aria-labelledby="rql-pcb-history-title">
      <div className="rql-pcb__history-head">
        <h2 id="rql-pcb-history-title" className="rql-pcb__history-title">
          {tabIcon}
          Recent activity with {agentName}
        </h2>
        <div className="rql-pcb__history-tabs" role="tablist" aria-label="Recent activity">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`rql-pcb__history-tab${tab === t.key ? ' rql-pcb__history-tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'commands' ? (
        tasks === null ? <HistorySkeleton /> : tasks.length === 0 ? (
          <p className="rql-pcb__history-empty">
            {tasksFailed
              ? `Couldn't load commands for ${agentName}.`
              : `No commands for ${agentName} in the last 7 days.`}
          </p>
        ) : (
          <ul className="rql-pcb__history-list">
            {tasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  className="rql-pcb__history-item"
                  onClick={() => { void openTask(task); }}
                  aria-busy={openingTaskId === String(task.id)}
                  disabled={!!openingTaskId}
                >
                  <span className="rql-pcb__history-main">
                    <span className="rql-pcb__history-name">
                      {task.task_number && <span className="rql-pcb__history-num">{task.task_number}</span>}
                      {plainText(task.title) || 'Untitled command'}
                    </span>
                    {plainText(task.description) && (
                      <span className="rql-pcb__history-desc">{plainText(task.description)}</span>
                    )}
                  </span>
                  <StatusTag status={task.status_id ? TASK_STATUS[task.status_id.toLowerCase()] ?? null : null} />
                  <span className="rql-pcb__history-date">{formatChatDate(parseKanbanDate(task.created_at))}</span>
                  <FiChevronRight size={13} className="rql-pcb__history-arrow" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )
      ) : tab === 'runs' ? (
        runs === null ? <HistorySkeleton /> : runs.length === 0 ? (
          <p className="rql-pcb__history-empty">
            {runsFailed
              ? `Couldn't load routine runs for ${agentName}.`
              : `No routine runs for ${agentName} in the last 7 days.`}
          </p>
        ) : (
          <ul className="rql-pcb__history-list">
            {runs.map((run) => (
              <li
                key={run.id}
                onMouseEnter={(e) => showNotesCard(e.currentTarget, run)}
                onMouseLeave={hideNotesCard}
                onFocus={(e) => showNotesCard(e.currentTarget, run)}
                onBlur={hideNotesCard}
              >
                <button
                  type="button"
                  className="rql-pcb__history-item"
                  onClick={() => openRun(run)}
                  disabled={!run.routine_id}
                  aria-describedby={notesCard?.runId === String(run.id) ? 'rql-pcb-run-notes' : undefined}
                >
                  <span className="rql-pcb__history-main">
                    <span className="rql-pcb__history-name">{plainText(run.name) || runNotesSummary(run.notes)}</span>
                    {run.name && run.notes && (
                      <span className="rql-pcb__history-desc">{runNotesSummary(run.notes)}</span>
                    )}
                  </span>
                  <StatusTag status={runStatus(run.status)} />
                  <span className="rql-pcb__history-date">{formatChatDate(parseApiDate(run.started_at || run.finished_at))}</span>
                  <FiChevronRight size={13} className="rql-pcb__history-arrow" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )
      ) : chats === null ? (
        <HistorySkeleton />
      ) : chats.length === 0 ? (
        <p className="rql-pcb__history-empty">
          {loadFailed
            ? `Couldn't load your chats with ${agentName}.`
            : `No chats with ${agentName} yet.`}
        </p>
      ) : (
        <ul ref={listRef} className="rql-pcb__history-list" aria-busy={loadingMore}>
          {chats.map((chat) => (
            <li key={chat.id}>
              <button type="button" className="rql-pcb__history-item" onClick={() => openChat(chat)}>
                <span className="rql-pcb__history-main">
                  <span className="rql-pcb__history-name">{plainText(chat.title) || 'Untitled chat'}</span>
                  {plainText(chat.description) && (
                    <span className="rql-pcb__history-desc">{plainText(chat.description)}</span>
                  )}
                </span>
                <span className="rql-pcb__history-date">{formatChatDate(chat.created)}</span>
                <FiChevronRight size={13} className="rql-pcb__history-arrow" aria-hidden="true" />
              </button>
            </li>
          ))}
          {loadingMore && (
            <li className="rql-pcb__history-skel" aria-hidden="true">
              <div className="chat-skel-bone" style={{ width: '45%', height: 12 }} />
              <div className="chat-skel-bone" style={{ width: '18%', height: 10 }} />
            </li>
          )}
          {hasMore && !loadingMore && (
            <li ref={sentinelRef} className="rql-pcb__history-more">
              <button type="button" className="rql-pcb__history-more-btn" onClick={loadMore}>
                {moreFailed ? "Couldn't load more — try again" : 'Load more chats'}
              </button>
            </li>
          )}
          {!hasMore && chats.length > PAGE_SIZE && (
            <li className="rql-pcb__history-end">That's all your chats with {agentName}.</li>
          )}
        </ul>
      )}
      {notesCard && createPortal(
        <div
          ref={notesCardRef}
          id="rql-pcb-run-notes"
          role="tooltip"
          className={`rql-pcb__notes-card${notesCard.above ? ' rql-pcb__notes-card--above' : ''}`}
          style={{ top: notesCard.top, left: notesCard.left, width: notesCard.width }}
          onMouseEnter={keepNotesCard}
          onMouseLeave={hideNotesCard}
        >
          <div className="rql-pcb__notes-card-title">{notesCard.title}</div>
          <div className="rql-pcb__notes-card-body">
            {notesCard.lines.map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default AgentChatHistory;
